import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";

type Props = {
  form: any; initial?: any; onSubmit?: (answers: any) => Promise<void>; preview?: boolean;
  submitLabel?: string; children?: React.ReactNode; onSkip?: () => void; testid?: string;
};

const slug = (o: string) => o.replace(/\s+/g, "-").toLowerCase();

function Chips({ options, isOn, onPick, tid }: { options: string[]; isOn: (o: string) => boolean; onPick: (o: string) => void; tid: string }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={tid}>
      {options.map((o) => (
        <button type="button" key={o} onClick={() => onPick(o)}
          className={`min-h-[40px] rounded-sm border px-3 py-1.5 text-sm transition-colors ${isOn(o) ? "border-emerald-600 bg-emerald-600 text-white" : "hover:border-emerald-500"}`}
          data-testid={`${tid}-option-${slug(o)}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

function FieldInput({ f, value, onChange }: { f: any; value: any; onChange: (v: any) => void }) {
  const tid = `quote-field-${f.id}`;
  if (f.type === "select")
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="h-11" data-testid={tid}><SelectValue placeholder="Choose one" /></SelectTrigger>
        <SelectContent>{f.options.map((o: string) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  if (f.type === "multiselect") {
    const arr: string[] = Array.isArray(value) ? value : [];
    return <Chips options={f.options} tid={tid} isOn={(o) => arr.includes(o)} onPick={(o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o])} />;
  }
  if (f.type === "yesno") return <Chips options={["Yes", "No"]} tid={tid} isOn={(o) => value === o} onPick={(o) => onChange(value === o ? "" : o)} />;
  if (f.type === "longtext") return <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  if (f.type === "date") return <DateField value={value || ""} onChange={onChange} testid={tid} />;
  return (
    <Input type={f.type === "number" ? "number" : "text"} inputMode={f.type === "number" ? "numeric" : undefined} value={value ?? ""} className="h-11"
      onChange={(e) => onChange(f.type === "number" && e.target.value !== "" ? Number(e.target.value) : e.target.value)} data-testid={tid} />
  );
}

export function QuoteForm({ form, initial, onSubmit, preview, submitLabel, children, onSkip, testid = "quote-form" }: Props) {
  const [answers, setAnswers] = useState<any>(initial || {});
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onSubmit) return;
    setBusy(true);
    try {
      await onSubmit(answers);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid={testid}>
      {form.fields.map((f: any) => (
        <div key={f.id} className="space-y-2">
          <Label className="text-sm font-semibold">
            {f.label} {f.required && <span className="text-rose-600">*</span>}
          </Label>
          {f.help && <p className="text-xs text-muted-foreground">{f.help}</p>}
          <FieldInput f={f} value={answers[f.id]} onChange={(v) => setAnswers({ ...answers, [f.id]: v })} />
        </div>
      ))}
      {children}
      {!preview && (
        <div className="flex gap-2">
          <Button type="submit" disabled={busy} className="h-11 flex-1 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`${testid}-submit-button`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel || (initial ? "Update my answers" : "Send to vendor")}
          </Button>
          {onSkip && <Button type="button" variant="ghost" className="h-11" onClick={onSkip} data-testid={`${testid}-skip-button`}>Skip</Button>}
        </div>
      )}
    </form>
  );
}

const show = (v: any) => (Array.isArray(v) ? (v.length ? v.join(", ") : "—") : v === undefined || v === null || v === "" ? "—" : String(v));

export function AnswersView({ answers, testid = "quote-answers-view", empty = "No questionnaire answers yet." }: { answers: any; testid?: string; empty?: string }) {
  if (!answers) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <dl className="space-y-3" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">{answers.form_name}</p>
      {(answers.fields || []).map((f: any) => (
        <div key={f.id} className="rounded-lg border p-3">
          <dt className="text-xs text-muted-foreground">{f.label}</dt>
          <dd className="text-sm font-medium">{show(answers.answers?.[f.id])}</dd>
        </div>
      ))}
    </dl>
  );
}

export { show as showAnswer };
